import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createContext, useCallback, useContext, useState } from "react";
import { compactPaneWidth } from "work/_views/work-pane-layout";

/**
 * The work pane's measured inline size, for the parts of a page that cannot ask in CSS. Undefined
 * means nobody is measuring — a tab rendered outside a work page, a browser without
 * `ResizeObserver`, or a unit test — and readers fall back to the viewport, which is the question they
 * asked before.
 */
export const WorkPaneWidthContext = createContext<number | undefined>(undefined);

export type WorkPaneSize = {
  /** Attach to the element that owns the pane — the same element that declares the CSS container. */
  paneRef: (node: HTMLElement | null) => void;
  /** Publish through `WorkPaneWidthContext` so the tabs can read what the page measured. */
  paneWidth: number | undefined;
  /**
   * True while the pane is too narrow to seat the page's controls at their full size. The CSS half of
   * the same question is `paneIsCompact(theme)`; both read the pane's inline size against the same
   * threshold, so a control whose two forms are different elements and a control that only restyles
   * itself change over together.
   */
  isCompact: boolean;
};

/**
 * The measured counterpart to the container queries in `work-pane-layout`.
 *
 * Most of what narrows on these pages can be said in CSS, but some of it cannot: the header's Save is
 * an icon with a caption below `md` and a labelled button above it — different elements, not one
 * element restyled — every action button picks a Material `size`, which is a prop, and the fee-schedule
 * and team tables drop whole columns. Those were asking `useMediaQuery(down("md"))`, which answers
 * about the window. The window is not the pane: the navigation drawer holds 17rem of it from `lg` up
 * and the page's own drawer another 400px, so opening comments on a 1280px display left full-size
 * controls and every table column in a 608px pane (#4964).
 *
 * The first render has nothing measured yet, so readers answer from the viewport — the old behaviour,
 * and the closer of the two guesses, since the pane is never wider than the window. The observer
 * corrects it as soon as the element mounts.
 */
export default function useWorkPaneSize(): WorkPaneSize {
  const theme = useTheme();
  const viewportIsCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [paneWidth, setPaneWidth] = useState<number | undefined>(undefined);

  const paneRef = useCallback((node: HTMLElement | null) => {
    // React 19 retires a ref by calling the cleanup this returns rather than by calling the ref with
    // null, so the detach path lives below. The guard is for the null call React makes when a render
    // returned no cleanup — which is what happens without ResizeObserver.
    if (!node) return;

    // jsdom has no ResizeObserver, and neither does a browser old enough to lack container queries.
    // Both then keep answering from the viewport, which is the behaviour these pages already had.
    if (typeof ResizeObserver === "undefined") return;

    // The observer reports once on `observe`, so the mount-time width arrives without measuring here
    // as well — and it arrives as the same box a container query resolves against, the content box.
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setPaneWidth(entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      // Carrying the width across an unmount would let the next page's first paint size itself
      // against the pane this one happened to have.
      setPaneWidth(undefined);
    };
  }, []);

  return {
    paneRef,
    paneWidth,
    isCompact: paneWidth === undefined ? viewportIsCompact : paneWidth < compactPaneWidth(theme),
  };
}

/**
 * For anything inside a work page's tabs that has to answer "is there room" in JavaScript. Reads
 * what the page measured; falls back to the viewport where no page is measuring.
 */
export function useWorkPaneNarrowerThan(width: number): boolean {
  const paneWidth = useContext(WorkPaneWidthContext);
  const viewportIsNarrower = useMediaQuery(`(max-width: ${width - 0.05}px)`);
  return paneWidth === undefined ? viewportIsNarrower : paneWidth < width;
}

export function useWorkPaneIsCompact(): boolean {
  const theme = useTheme();
  return useWorkPaneNarrowerThan(compactPaneWidth(theme));
}
